// CarouselView — the presentational "film-strip" Image Carousel renderer for the
// DL-041 LOCKED deck spec. It is deliberately craft.js-free and Redux-free so it
// renders identically in the builder, the published Segment view, and (later)
// the mobile WebView. All WHAT-decisions come from ./carouselLogic (unit-tested);
// this file only owns the DOM, refs, measurement and CSS.
//
// Deck spec honoured here:
//   * Film strip: every image keeps its OWN shape at a SHARED height (no crop,
//     no letterbox bars) — Fit = "Equalize & Keep Shape" is the default; Crop /
//     Fill are the equalised alternatives.
//   * Gapped cards with a gradient border, the active card centred with a small
//     PEEK of the neighbours on either side.
//   * Manual arrows (+ optional dots) by DEFAULT; Auto advances on its own at
//     the chosen speed, PAUSES when scrolled off-screen, and is fully
//     suppressed under prefers-reduced-motion (reduced-motion => no auto-advance).
//   * QA-113 item 6 (Kelly, bug #513): navigation is ARROW-ONLY — no swipe/drag
//     gesture nav. A pointer-drag inside the strip used to also change slides,
//     which both fought the block's own craft.js drag-to-reorder handle in the
//     builder AND was never discoverable (its one hint, "Drag or swipe", was
//     already removed under item 8) — an undocumented, accidental nav path.
//     The arrow buttons (and optional dots) are the only ways to change slides.
//   * Lightweight: only the current + immediate-neighbour images carry a real
//     src (the rest stay unfetched); movement is a GPU transform; no animation
//     library. Reduced-motion also kills the slide transition (instant snap).
//   * Keyboard: Left/Right step, Home/End jump; every control is a real button
//     with an aria-label; a polite live-region announces the current slide.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { fontFamily, brand } from "../../../../theme/tokens";
import {
  clampIndex,
  fitToObjectFit,
  isSlideLoaded,
  isUniformCell,
  keyToIndex,
  loopWrapEdge,
  nextIndex,
  prevIndex,
  shouldAutoAdvance,
  speedToMs,
} from "./carouselLogic";
import { getSafeHref } from "../../../../utils/safeHref";
import ImageModal from "../../../ImageModal";

// ── Motion / theme constants (deck tokens) ───────────────────────────────────
const GRADIENT = `linear-gradient(135deg, ${brand.primary} 0%, #6D5EF6 100%)`;
const SLIDE_TRANSITION = "transform 420ms cubic-bezier(0.22, 0.61, 0.36, 1)";

// ── styled shells ─────────────────────────────────────────────────────────────
const Viewport = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  font-family: ${fontFamily};
  outline: none;
  /* QA-113 item 6: navigation is arrow-only — no swipe/drag gesture nav — so
     touch scrolling is left to the browser's default in both directions. */
  &:focus-visible {
    box-shadow: 0 0 0 3px ${brand.focusRing};
    border-radius: 12px;
  }
`;

const Track = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  will-change: transform;
  transform: translate3d(${(p) => p.$offset}px, 0, 0);
  transition: ${(p) => (p.$reduced ? "none" : SLIDE_TRANSITION)};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Slide = styled.div`
  flex: 0 0 auto;
  height: ${(p) => p.$height}px;
  width: ${(p) => (p.$uniformW ? `${p.$uniformW}px` : "auto")};
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Gradient-border card via the padding-box / border-box background trick — a real
// gradient ring with no pseudo-element hacks. Inactive cards drop to a faint solid
// ring + a slight fade + scale-down so the active card reads as the focus, exactly
// like the deck's peeked neighbours.
const Card = styled.div`
  position: relative;
  height: 100%;
  width: ${(p) => (p.$uniformW ? "100%" : "auto")};
  border-radius: ${(p) => p.$radius + 3}px;
  padding: 3px;
  box-sizing: border-box;
  background: ${(p) => (p.$active ? GRADIENT : "#E4E7EC")};
  opacity: ${(p) => (p.$active ? 1 : 0.72)};
  transform: scale(${(p) => (p.$active ? 1 : 0.92)});
  transform-origin: center;
  transition: ${(p) =>
    p.$reduced ? "none" : "opacity 300ms ease, transform 300ms ease"};
  box-shadow: ${(p) =>
    p.$active
      ? "0 8px 24px rgba(18,48,81,0.18)"
      : "0 2px 8px rgba(18,48,81,0.08)"};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const CardInner = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  border-radius: ${(p) => p.$radius}px;
  overflow: hidden;
  background: #f2f4f7;
`;

const SlideImg = styled.img`
  display: block;
  height: 100%;
  width: ${(p) => (p.$uniform ? "100%" : "auto")};
  object-fit: ${(p) => p.$objectFit};
  user-select: none;
  -webkit-user-drag: none;
`;

// Neutral box shown for a not-yet-loaded slide (keeps the strip length stable
// before the real image fetches). Sized to a gentle portrait-ish default width.
const SlidePlaceholder = styled.div`
  height: 100%;
  width: ${(p) => p.$w}px;
  border-radius: ${(p) => p.$radius}px;
  background: linear-gradient(90deg, #eef0f3 0%, #f6f7f9 50%, #eef0f3 100%);
`;

// Designed fallback shown when a slide's media URL fails to load (broken/expired
// src). Replaces the browser's native broken-image glyph with a calm, framed
// "image unavailable" state that keeps the film-strip cell shape stable. Uniform
// fits fill the fixed cell; keep-shape falls back to the same gentle default
// width the loading placeholder uses so the strip length never jumps.
const BrokenSlide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  width: ${(p) => (p.$uniform ? "100%" : `${p.$w}px`)};
  padding: 0 12px;
  box-sizing: border-box;
  background: #f2f4f7;
  color: #98a2b3;
  font-family: ${fontFamily};
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  user-select: none;
  svg {
    width: 32px;
    height: 32px;
  }
`;

const CaptionPill = styled.span`
  position: absolute;
  left: 10px;
  bottom: 10px;
  max-width: calc(100% - 20px);
  padding: 4px 10px;
  border-radius: 9999px;
  background: rgba(18, 48, 81, 0.82);
  color: #fff;
  font-family: ${fontFamily};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// QA-63 (Kelly, 2026-08-03): "I want the ability to be able to open these images
// up all the way ... you can do that on production, but outside of carousel, but
// I want in the carousel ... And when it opens up, like you saw in edit, I need
// the captions to be there and the ability also while it's open to be able to
// toggle across the images." (She noted she has asked "multiple times".)
// Mirrors LinkChip's geometry on the opposite corner so the active card carries
// at most two small round controls, never a new visual language.
const EnlargeChip = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: ${brand.primary};
  box-shadow: 0 2px 6px rgba(18, 48, 81, 0.2);
  cursor: pointer;
  z-index: 2;
  &:hover {
    background: #fff;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${brand.focusRing};
  }
  svg {
    width: 16px;
    height: 16px;
  }
`;

const LinkChip = styled.a`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: ${brand.primary};
  box-shadow: 0 2px 6px rgba(18, 48, 81, 0.2);
  text-decoration: none;
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${brand.focusRing};
  }
  svg {
    width: 16px;
    height: 16px;
  }
`;

// QA-113 (Kelly, 2026-08-06): the next/prev control is a PLAIN ARROWHEAD, not a
// round ‹ › "bubble" — the old white circle+shadow was "too big and disrupts the
// image". A bare white chevron + drop-shadow was tried for legibility over any
// photo, but MEASURED live (kelly-0929-1408): on a light/white-background image
// the drop-shadow silhouette alone is too faint to read — the arrow all but
// disappears (see kelly0929-arrow-contrast-current.png). Fix: a small
// translucent dark "contrast chip" behind the glyph — same 32px footprint as
// before (no bigger, so QA-113's "too big" complaint doesn't return), but
// unlike the old bubble it's a soft tinted glass circle, not an opaque white
// disc, so it reads as a control rather than a shape stamped on the photo.
// Guarantees contrast against ANY image color/brightness, not just busy ones.
const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  ${(p) => (p.$side === "left" ? "left: 6px;" : "right: 6px;")}
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(18, 48, 81, 0.45);
  box-shadow: 0 2px 6px rgba(18, 48, 81, 0.3);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  transition: transform 0.2s ease, opacity 0.2s ease, background-color 0.2s ease;
  &:hover {
    transform: translateY(-50%) scale(1.12);
    background: rgba(18, 48, 81, 0.6);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${brand.focusRing};
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  svg {
    width: 20px;
    height: 20px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 14px;
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Dot = styled.button`
  width: ${(p) => (p.$active ? "22px" : "8px")};
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: ${(p) => (p.$active ? brand.primary : "#cfd4dc")};
  cursor: pointer;
  transition: ${(p) =>
    p.$reduced ? "none" : "width 0.25s ease, background-color 0.2s ease"};
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${brand.focusRing};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const EmptyCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${(p) => p.$height}px;
  border: 2px dashed #d0bffb;
  border-radius: 12px;
  background: #faf7ff;
  color: #7b3fd6;
  font-family: ${fontFamily};
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  padding: 0 16px;
`;

const SrOnly = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// ── inline line-art icons (never emoji, G-19-adjacent icon law) ───────────────
const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const LinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M10 14a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 10a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l1-1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
// Standard "expand / open-in-full" glyph — two diagonal arrows pointing outward
// (QA-113, Kelly 2026-08-06: the old square-corner brackets read as a crop mark;
// replace with the universally-recognised enlarge arrows). Line-art, never emoji.
const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
// Framed-image glyph crossed by a diagonal slash — the classic "broken image"
// mark, drawn as clean line-art (never emoji, G-19-adjacent icon law).
const BrokenImageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M3 15.5l4-4 3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 14l2.5-2.5L21 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 4.5l15.5 15.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(!!mq.matches);
    apply();
    // addEventListener is the modern API; fall back to addListener for old Safari.
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else if (mq.addListener) mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else if (mq.removeListener) mq.removeListener(apply);
    };
  }, []);
  return reduced;
};

export default function CarouselView({
  images = [],
  mode = "manual",
  speed = "medium",
  fit = "keep-shape",
  height = 260,
  showCaptions = false,
  showArrows = true,
  showDots = true,
  borderRadius = 12,
  hyperlink = "",
  showHyperlink = false,
  loop = true,
  editing = false,
  emptyLabel = "Add images to build your carousel",
}) {
  const count = images.length;
  const [current, setCurrent] = useState(0);
  const [offset, setOffset] = useState(0);
  const [inView, setInView] = useState(true);
  // Set of media srcs whose <img> fired onError — those slides render the
  // designed broken-media fallback instead of the native broken-image glyph.
  // Keyed by src (not index) so a reordered/duplicated broken URL stays flagged;
  // swapping <img> for a <div> means the error path can never loop.
  const [brokenSrcs, setBrokenSrcs] = useState(() => new Set());
  // Which slide the lightbox is open on; null = closed. QA-63.
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const reduced = useReducedMotion();
  // T22.163: seamless loop. While `wrap` is "tail"/"head" the track animates ONE
  // slide onward to an edge clone (a copy of the first image after the last, or of
  // the last before the first) instead of sliding back across the whole strip;
  // when that step lands, `snapping` drops the transition for one frame so the
  // track jumps invisibly from the clone to the identical real slide.
  const [wrap, setWrap] = useState(null);
  const [snapping, setSnapping] = useState(false);
  const loopClones = loop && count > 1;

  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const slideRefs = useRef([]);
  const headCloneRef = useRef(null);
  const tailCloneRef = useRef(null);

  // Keep current index valid when the image list shrinks.
  useEffect(() => {
    setCurrent((c) => clampIndex(c, count));
  }, [count]);

  const setSlideRef = useCallback((el, i) => {
    slideRefs.current[i] = el;
  }, []);

  // Flag a media src as broken on the first onError (idempotent — no re-render
  // storm if the same src re-mounts after being scrolled out of the load window).
  const handleImgError = useCallback((src) => {
    setBrokenSrcs((prev) => {
      if (prev.has(src)) return prev;
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  }, []);

  // ── centre the active slide (variable-width film-strip) ─────────────────────
  const recenter = useCallback(() => {
    const vp = viewportRef.current;
    const el =
      wrap === "tail"
        ? tailCloneRef.current
        : wrap === "head"
        ? headCloneRef.current
        : slideRefs.current[current];
    if (!vp || !el) return;
    const vpWidth = vp.clientWidth;
    const slideCenter = el.offsetLeft + el.offsetWidth / 2;
    setOffset(Math.round(vpWidth / 2 - slideCenter));
  }, [current, wrap]);

  useLayoutEffect(() => {
    recenter();
  }, [recenter, count, fit, height]);

  // Re-centre on viewport resize AND on the active slide's own box changing.
  // A "keep shape" (non-uniform) slide's width is auto — it starts at the
  // loading placeholder's width and jumps to the loaded <img>'s real
  // intrinsic width once the fetch resolves. That resize happens on the
  // SLIDE element, not the viewport, so a viewport-only observer never
  // re-fires and the placeholder-based offset ships uncorrected (measured
  // on ViewWheel's segment side-drawer: active slide's right edge overflows
  // the panel by 161px, unrecovered even after a forced window resize,
  // because a fixed-width side-drawer's own box doesn't change on window
  // resize either — builder canvas hides this because its images are
  // already cached by the time recenter() first runs).
  useEffect(() => {
    const vp = viewportRef.current;
    const activeEl = slideRefs.current[current];
    if (!vp || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => recenter());
    ro.observe(vp);
    if (activeEl) ro.observe(activeEl);
    return () => ro.disconnect();
  }, [recenter, current]);

  // ── off-screen pause (IntersectionObserver) ─────────────────────────────────
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setInView(e.isIntersecting);
      },
      { threshold: 0.25 }
    );
    io.observe(vp);
    return () => io.disconnect();
  }, []);

  // ── step one slide (arrows, arrow keys, auto-advance) ───────────────────────
  // A step that wraps an end of a looping strip goes to the edge clone (T22.163).
  // Reduced motion already snaps instantly, so it never needs the clone. A step
  // that arrives mid-wrap is dropped: the wrap lands within one transition.
  const step = useCallback(
    (dir) => {
      if (wrap) return;
      const target =
        dir > 0 ? nextIndex(current, count, loop) : prevIndex(current, count, loop);
      const edge =
        loopClones && !reduced ? loopWrapEdge(current, target, count, dir) : null;
      if (edge) setWrap(edge);
      setCurrent(target);
    },
    [wrap, current, count, loop, loopClones, reduced]
  );
  const stepRef = useRef(step);
  stepRef.current = step;

  const finishWrap = useCallback(() => {
    setSnapping(true);
    setWrap(null);
  }, []);

  // Land the wrap on transitionend; the timer is the fallback for a transition
  // that never fires its end event (tab hidden, element detached).
  useEffect(() => {
    if (!wrap) return undefined;
    const id = setTimeout(finishWrap, 600);
    return () => clearTimeout(id);
  }, [wrap, finishWrap]);

  // Re-enable the slide transition only after the no-transition snap has painted.
  useEffect(() => {
    if (!snapping) return undefined;
    let id2;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setSnapping(false));
    });
    return () => {
      cancelAnimationFrame(id1);
      if (id2) cancelAnimationFrame(id2);
    };
  }, [snapping]);

  const onTrackTransitionEnd = (e) => {
    if (wrap && e.target === e.currentTarget && e.propertyName === "transform")
      finishWrap();
  };

  // ── auto-advance (guarded by shouldAutoAdvance) ─────────────────────────────
  useEffect(() => {
    if (!shouldAutoAdvance({ mode, reducedMotion: reduced, inView, count, editing }))
      return undefined;
    const id = setInterval(() => stepRef.current(1), speedToMs(speed));
    return () => clearInterval(id);
  }, [mode, reduced, inView, count, editing, speed, loop]);

  const goTo = useCallback(
    (idx) => setCurrent(clampIndex(idx, count)),
    [count]
  );
  const goNext = useCallback(() => step(1), [step]);
  const goPrev = useCallback(() => step(-1), [step]);

  // ── keyboard ────────────────────────────────────────────────────────────────
  const onKeyDown = (e) => {
    const idx = keyToIndex(e.key, current, count, loop);
    if (idx === null) return;
    e.preventDefault();
    if (e.key === "ArrowRight") step(1);
    else if (e.key === "ArrowLeft") step(-1);
    else setCurrent(idx);
  };

  // ── empty state (a designed state, not the happy path) ──────────────────────
  if (count === 0) {
    return (
      <EmptyCard $height={height} role="note">
        {emptyLabel}
      </EmptyCard>
    );
  }

  const objectFit = fitToObjectFit(fit);
  const uniform = isUniformCell(fit);
  // Scheme-validate the hyperlink at render — the block's node props deserialize
  // from the saved-wheel API unsanitized, so a crafted `javascript:`/`data:`/
  // protocol-relative value must never reach the LinkChip href (CAR-F2).
  const safeHyperlink = getSafeHref(hyperlink);
  const uniformW = uniform ? Math.round(height * (4 / 3)) : null;
  const placeholderW = Math.round(height * 0.72);
  const activeImage = images[clampIndex(current, count)] || {};
  const liveMode = mode === "manual" ? "polite" : "off";
  const motionOff = reduced || snapping;

  // Decorative copy of a real slide at one end of the strip (T22.163). Hidden from
  // assistive tech, no controls; it only ever shows while the track glides onto it.
  const renderClone = (edge) => {
    const i = edge === "tail" ? 0 : count - 1;
    const img = images[i];
    const active = wrap === edge;
    const showImg =
      isSlideLoaded(i, current, count, loop) && !brokenSrcs.has(img.src);
    return (
      <Slide
        key={`loop-clone-${edge}`}
        ref={edge === "tail" ? tailCloneRef : headCloneRef}
        $height={height}
        $uniformW={uniformW}
        aria-hidden={true}
        data-loop-clone={edge}
      >
        <Card
          $active={active}
          $radius={borderRadius}
          $uniformW={uniformW}
          $reduced={motionOff}
        >
          <CardInner $radius={borderRadius}>
            {showImg ? (
              <SlideImg
                src={img.src}
                alt=""
                $objectFit={objectFit}
                $uniform={uniform}
                loading="lazy"
                draggable={false}
              />
            ) : (
              <SlidePlaceholder $w={uniformW || placeholderW} $radius={borderRadius} />
            )}
            {active && showCaptions && img.title ? (
              <CaptionPill>{img.title}</CaptionPill>
            ) : null}
          </CardInner>
        </Card>
      </Slide>
    );
  };

  return (
    <Viewport
      ref={viewportRef}
      tabIndex={0}
      role="group"
      aria-roledescription="carousel"
      aria-label="Image carousel"
      onKeyDown={onKeyDown}
    >
      <Track
        ref={trackRef}
        $offset={offset}
        $reduced={motionOff}
        onTransitionEnd={onTrackTransitionEnd}
      >
        {loopClones ? renderClone("head") : null}
        {images.map((img, i) => {
          const active = i === current;
          const loaded = isSlideLoaded(i, current, count, loop);
          return (
            <Slide
              key={img.id != null ? img.id : i}
              ref={(el) => setSlideRef(el, i)}
              $height={height}
              $uniformW={uniformW}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}`}
              aria-hidden={active ? undefined : true}
            >
              <Card
                $active={active}
                $radius={borderRadius}
                $uniformW={uniformW}
                $reduced={motionOff}
              >
                <CardInner $radius={borderRadius}>
                  {loaded ? (
                    brokenSrcs.has(img.src) ? (
                      <BrokenSlide
                        $w={uniformW || placeholderW}
                        $uniform={uniform}
                        role="img"
                        aria-label={
                          img.title
                            ? `Image unavailable: ${img.title}`
                            : "Image unavailable"
                        }
                      >
                        <BrokenImageIcon />
                        <span>Image unavailable</span>
                      </BrokenSlide>
                    ) : (
                      <SlideImg
                        src={img.src}
                        alt={img.title || `Slide ${i + 1}`}
                        $objectFit={objectFit}
                        $uniform={uniform}
                        loading="lazy"
                        draggable={false}
                        onError={() => handleImgError(img.src)}
                      />
                    )
                  ) : (
                    <SlidePlaceholder $w={uniformW || placeholderW} $radius={borderRadius} aria-hidden="true" />
                  )}
                  {active && !brokenSrcs.has(img.src) && loaded ? (
                    <EnlargeChip
                      type="button"
                      aria-label={
                        img.title ? `Enlarge image: ${img.title}` : `Enlarge image ${i + 1}`
                      }
                      onClick={(e) => {
                        // In the builder this control sits inside a craft.js
                        // draggable; without this the click also selects/drags
                        // the block instead of opening the lightbox.
                        e.stopPropagation();
                        setLightboxIndex(i);
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <ExpandIcon />
                    </EnlargeChip>
                  ) : null}
                  {active && showCaptions && img.title ? (
                    <CaptionPill>{img.title}</CaptionPill>
                  ) : null}
                  {active && showHyperlink && safeHyperlink ? (
                    <LinkChip
                      href={safeHyperlink}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open link for ${img.title || "this image"}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <LinkIcon />
                    </LinkChip>
                  ) : null}
                </CardInner>
              </Card>
            </Slide>
          );
        })}
        {loopClones ? renderClone("tail") : null}
      </Track>

      {showArrows && count > 1 ? (
        <>
          <ArrowBtn
            type="button"
            $side="left"
            aria-label="Previous image"
            onClick={goPrev}
            disabled={!loop && current === 0}
          >
            <ChevronLeft />
          </ArrowBtn>
          <ArrowBtn
            type="button"
            $side="right"
            aria-label="Next image"
            onClick={goNext}
            disabled={!loop && current === count - 1}
          >
            <ChevronRight />
          </ArrowBtn>
        </>
      ) : null}

      {showDots && count > 1 ? (
        <Controls>
          <Dots role="tablist" aria-label="Choose image">
            {images.map((img, i) => (
              <Dot
                key={img.id != null ? img.id : i}
                type="button"
                role="tab"
                aria-selected={i === current}
                aria-label={`Go to image ${i + 1}`}
                $active={i === current}
                $reduced={reduced}
                onClick={() => goTo(i)}
              />
            ))}
          </Dots>
        </Controls>
      ) : null}

      <SrOnly aria-live={liveMode} aria-atomic="true">
        {`Image ${current + 1} of ${count}${activeImage.title ? `: ${activeImage.title}` : ""}`}
      </SrOnly>

      {/* QA-63: the lightbox gets the WHOLE strip, not just the one slide, so the
          arrows inside it step through the carousel — Kelly asked to "toggle
          across the images" while enlarged. showTitle follows the caption's
          existence, exactly like ImageBlock's enlarge rule (QA-54 / PR #2498),
          so a caption never disappears just because it was enlarged. */}
      <ImageModal
        open={lightboxIndex !== null}
        startIndex={lightboxIndex === null ? 0 : lightboxIndex}
        images={images.map((img) => ({ src: img.src, title: img.title || "" }))}
        showTitle={images.some((img) => !!img.title)}
        handleClose={() => setLightboxIndex(null)}
      />
    </Viewport>
  );
}
